import lookAndFeel from "@/config/lookAndFeel";
import { registerCorePlugins } from "@/plugins/design-system";
import * as Sentry from "@sentry/vue";
import { createBootstrap } from "bootstrap-vue-next";
import { createApp } from "vue";
import { createVuetify } from "vuetify";
import { VBtn, VSnackbar } from "vuetify/components";
import App from "./App.vue";
import { registerPlugins } from "./plugins";

const vuetify = createVuetify({
  components: {
    VSnackbar,
    VBtn,
  },
});

function createLink(rel: string, href: string, sizes?: string, type?: string) {
  const link = document.createElement("link");
  link.rel = rel;
  link.href = href;
  if (sizes) link.sizes = sizes;
  if (type) link.type = type;
  return link;
}

// Set favicons dynamically
const head = document.querySelector("head") as HTMLHeadElement;
if (head) {
  if (lookAndFeel.faviconFileUrl) {
    head.appendChild(createLink("icon", lookAndFeel.faviconFileUrl, "any", "image/png"));
  }
  if (lookAndFeel.faviconAppleTouchFileUrl) {
    head.appendChild(createLink("icon", lookAndFeel.faviconAppleTouchFileUrl, "180x180"));
  }
  if (lookAndFeel.favicon32FileUrl) {
    head.appendChild(createLink("icon", lookAndFeel.favicon32FileUrl, "32x32", "image/png"));
  }
  if (lookAndFeel.favicon16FileUrl) {
    head.appendChild(createLink("icon", lookAndFeel.favicon16FileUrl, "16x16", "image/png"));
  }
  if (lookAndFeel.manifestFileUrl) {
    head.appendChild(createLink("manifest", lookAndFeel.manifestFileUrl));
  }
}

// Register service worker
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("/sw.js").then(
    () => {
      console.warn("[ServiceWorker]: Registration successful");
    },
    (err) => {
      console.warn("[ServiceWorker]: Registration failed");
      console.warn(err);
    },
  );
  navigator.serviceWorker.getRegistration().then(function (reg) {
    // There's an active SW, but no controller for this tab.
    if (reg?.active && !navigator.serviceWorker.controller) {
      // Perform a soft reload to load everything from the SW and get
      // a consistent set of resources.
      window.location.reload();
    }
  });
}

// Init Vue application
(async () => {
  const app = createApp(App);

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    sendDefaultPii: false,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    enableLogs: true,
  });

  // Register local design system core plugins
  registerCorePlugins(app);

  // Register host app plugins
  registerPlugins(app);
  app.use(vuetify);
  app.use(createBootstrap());

  // Mount app
  app.mount("#app");
})();
